/**
 * PI Barra — hablar con el edge.
 *
 * El transporte y nada más: URLs, token y errores. Las decisiones sobre QUÉ
 * pedir viven en `fuente.ts`, que es la puerta única del kiosco y del admin.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * DÓNDE VIVE EL TOKEN — Y NO ES LO MISMO EN LAS DOS SUPERFICIES
 * ═══════════════════════════════════════════════════════════════════════════
 * El default es `sessionStorage`, y la razón es el KIOSCO: su panel se abre en
 * la LAPTOP DEL VENUE, que se queda en la barra. Con `localStorage` la sesión
 * sobreviviría a cerrar el navegador, apagar la laptop y al turno siguiente.
 * Con `sessionStorage` muere al cerrar la pestaña.
 *
 * El panel de la BARRA pide `localStorage` con `usarAlmacen("local")`, y es la
 * decisión correcta ahí por el mismo tipo de razonamiento, no a pesar de él:
 * corre como PWA en el teléfono PROPIO del bartender, que se bloquea cada dos
 * minutos y descarga la app de memoria. Con `sessionStorage` tendría que
 * teclear la frase del venue cada vez que mira la pantalla — y una herramienta
 * que cuesta eso no se abre, se deja de usar.
 *
 * Un teléfono personal no es una laptop compartida. Lo que acota el riesgo en
 * los dos casos es lo mismo: el token vive 12 horas del lado del SERVIDOR
 * (`pos_token_horas`), que es la mitad del candado que un navegador no puede
 * saltarse.
 */

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * LA BASE YA NO ES UNA CONSTANTE DE MÓDULO. LA PONE QUIEN ARRANCA
 * ═══════════════════════════════════════════════════════════════════════════
 * Hasta el paso 0 esto era `const BASE = import.meta.env["VITE_POS_API"]`, y
 * eso convertía la dirección del servidor en una decisión del BUILD. Dentro de
 * un APK eso obliga a recompilar y volver a firmar por cada venue.
 *
 * Hoy cada app la declara al arrancar con `configurarBase()`, y este archivo no
 * lee el env NUNCA. No es purismo: `kiosk/` corre también dentro del APK y ahí
 * la dirección sale del almacenamiento del aparato (`kiosk/src/configuracion.ts`).
 * Si este módulo conservara un respaldo al env, un `kiosk/.env.local` de la
 * máquina de quien compila —que Vite carga en TODOS los modos, incluido
 * `--mode android`— se colaría dentro del APK como dirección por omisión. Ese
 * archivo NO está en este worktree, pero sí en `.worktrees/f2-cobro` de esta
 * misma Mac, con `VITE_POS_API=http://127.0.0.1:8124` — y `127.0.0.1` dentro de
 * un Android es el propio Android. O sea: de qué worktree se compile decidiría
 * a qué servidor le habla el APK. Por eso el respaldo no existe.
 *
 * Quién llama a `configurarBase()`, y con qué:
 *
 *   `kiosk/src/main.tsx`   con `configuracion.ts`, que en la web lee el env y
 *                          en nativo lee `@capacitor/preferences`.
 *   `kds/src/main.tsx`     con `VITE_POS_API`. El KDS no corre en un APK.
 *
 * Sin esa llamada `enVivo()` es `false`, que es el lado correcto para fallar:
 * una app que no sabe a qué servidor hablarle lo DICE en vez de cobrar contra
 * uno que nadie eligió.
 */
let BASE = "";

const LLAVE_TOKEN = "tap:token";

/**
 * Declara a qué servidor le habla esta app. Se llama al ARRANCAR, antes del
 * primer `fetch`, y una sola vez.
 *
 * Cadena vacía significa "no hay edge", que es un estado declarado y no un
 * accidente: el kiosco corre como maqueta y el KDS enseña su pantalla de
 * "configúrame".
 */
export function configurarBase(url: string): void {
  BASE = url.trim().replace(/\/+$/, "");
}

/**
 * A qué servidor le está hablando ahora mismo.
 *
 * Existe para UN caso: la pantalla de configuración del APK prueba una base
 * CANDIDATA con `POST /auth/local` antes de guardarla, y si la frase no
 * coincide tiene que dejar el módulo como estaba. Sin esta lectura, un intento
 * fallido dejaría al kiosco apuntando a un servidor que nadie aceptó.
 */
export function baseActual(): string {
  return BASE;
}

// `session` por default. Ver la nota de arriba: la que elige es cada app, en su
// arranque, y la elección está justificada en las dos.
let almacen: () => Storage = () => sessionStorage;

export function usarAlmacen(cual: "session" | "local"): void {
  almacen = cual === "local" ? () => localStorage : () => sessionStorage;
}

/**
 * ¿Hay un edge al que hablarle?
 *
 * Sin base configurada el kiosco corre como MAQUETA: catálogo sembrado en
 * `localStorage`, nada persiste más allá de este navegador. Es un modo
 * explícito, no un respaldo silencioso — el distintivo SIMULACIÓN de la
 * pantalla lo dice, y `fuente.ts` no cae a la maqueta cuando el edge falla:
 * un catálogo inventado en una barra que cobra es exactamente lo que el repo
 * prohíbe.
 */
export function enVivo(): boolean {
  return BASE !== "";
}

export function urlDe(ruta: string): string {
  return `${BASE}${ruta}`;
}

export function token(): string | null {
  try {
    return almacen().getItem(LLAVE_TOKEN);
  } catch {
    // Safari en modo privado lanza al tocar el almacenamiento. Sin token no se
    // entra, que es el lado correcto para fallar.
    return null;
  }
}

export function guardarToken(t: string): void {
  almacen().setItem(LLAVE_TOKEN, t);
}

export function olvidarToken(): void {
  try {
    almacen().removeItem(LLAVE_TOKEN);
  } catch {
    /* ver `token()` */
  }
}

/** Un error del edge, con su código para que quien llame decida. */
export class ErrorDelEdge extends Error {
  constructor(
    readonly codigo: number,
    mensaje: string,
  ) {
    super(mensaje);
    this.name = "ErrorDelEdge";
  }

  /** 401/403: hay que volver a entrar. Lo distingue el panel para pedir la frase. */
  get esDePermiso(): boolean {
    return this.codigo === 401 || this.codigo === 403;
  }
}

async function leerError(r: Response): Promise<string> {
  try {
    const cuerpo = await r.json();
    const d = cuerpo?.detail;
    if (typeof d === "string") return d;
    if (d?.detail) return String(d.detail);
    return JSON.stringify(cuerpo).slice(0, 300);
  } catch {
    return `${r.status} ${r.statusText}`;
  }
}

/**
 * Una petición al edge. Con token si hay, sin token si no.
 *
 * `credentials: "omit"` a propósito: el edge autentica con `Authorization`, y
 * mandar cookies a una caja de la red local solo agrega superficie.
 */
/**
 * Qué decirle a quien está enfrente cuando la red no contestó.
 *
 * Es una función PURA y exportada para poder probarla: el `catch` del que sale
 * no se puede ejercitar sin un servidor caído, y un mensaje que manda a
 * revisar el aparato equivocado cuesta una visita al venue.
 */
export function diagnosticoDeRed(url: string): string {
  if (url.startsWith("http://")) {
    return (
      `no se pudo hablar con el servidor (${url}). Si esta es la app instalada ` +
      `desde Play, la dirección tiene que empezar con https:// — el APK de ` +
      `release no habla en claro. Si no, revisa que el servidor esté encendido.`
    );
  }
  return `no se pudo hablar con el servidor (${url}). ¿Está encendido?`;
}

export async function pedir<T>(
  ruta: string,
  opciones: { metodo?: string; cuerpo?: unknown; conToken?: boolean } = {},
): Promise<T> {
  const { metodo = "GET", cuerpo, conToken = true } = opciones;

  const cabeceras: Record<string, string> = {};
  if (cuerpo !== undefined) cabeceras["Content-Type"] = "application/json";
  const t = conToken ? token() : null;
  if (t) cabeceras["Authorization"] = `Bearer ${t}`;

  let r: Response;
  try {
    r = await fetch(urlDe(ruta), {
      method: metodo,
      headers: cabeceras,
      credentials: "omit",
      ...(cuerpo !== undefined ? { body: JSON.stringify(cuerpo) } : {}),
    });
  } catch (e) {
    // El edge no contesta: servidor apagado, red del venue caída, o el servicio
    // reiniciándose. Es un caso NORMAL en un antro y tiene que decirse como
    // tal, no como "error inesperado".
    //
    // Y HAY UNA CAUSA QUE NO ES EL SERVIDOR, Y MANDABA A REVISAR EL SERVIDOR.
    // Desde el paso 2, el APK de RELEASE niega TODO el tráfico en claro: su
    // `network_security_config.xml` quedó con `cleartextTrafficPermitted=false`
    // y sin una sola excepción de dominio. Una dirección `http://` en ese APK
    // falla aquí, en el `catch` de la red, exactamente igual que un mini PC
    // apagado — y el mensaje mandaba a revisar una caja que estaba bien. Se
    // distingue por el esquema, que es lo único que hace falta, y se dice.
    throw new ErrorDelEdge(0, diagnosticoDeRed(urlDe(ruta)));
  }

  if (r.status === 401 || r.status === 403) {
    // El token venció o el rol no alcanza. Se tira el token: dejarlo puesto
    // haría que cada acción siguiente fallara igual sin decir por qué.
    if (r.status === 401) olvidarToken();
    throw new ErrorDelEdge(r.status, await leerError(r));
  }
  if (!r.ok) throw new ErrorDelEdge(r.status, await leerError(r));

  if (r.status === 204) return undefined as T;
  return (await r.json()) as T;
}

// ── Entrar ──────────────────────────────────────────────────────────────────

export async function entrarConFrase(frase: string): Promise<void> {
  const r = await pedir<{ token?: unknown }>("/auth/local", {
    metodo: "POST",
    cuerpo: { frase },
    conToken: false,
  });

  // ═══════════════════════════════════════════════════════════════════════
  // SE MIRA EL CUERPO, NO SOLO EL CÓDIGO. 9 de septiembre de 2026.
  // ═══════════════════════════════════════════════════════════════════════
  // Antes esto era `pedir<{ token: string }>` y `guardarToken(r.token)`. Ese
  // `<{ token: string }>` es un MOLDE de TypeScript: no comprueba nada en
  // tiempo de ejecución. La revisión adversaria del paso 2 Android lo
  // reprodujo con un servidor impostor de doce líneas que solo contesta
  // `200 {}`: `entrarConFrase` resolvía, y `guardarToken(undefined)` guardaba
  // la cadena "undefined" sin lanzar.
  //
  // POR QUÉ IMPORTA MÁS DE LO QUE PARECE. En el kiosco de Android esta función
  // es la que valida la frase que SACA EL APARATO DEL MODO KIOSCO. Cinco
  // toques en la esquina abren la pantalla escondida sin frase, ahí se teclea
  // la dirección de un servidor propio, y con esto roto cualquier cosa que
  // conteste 200 desbloqueaba un panel atornillado a la pared de un antro. En
  // el panel de la BARRA el daño es otro y también real: un 200 sin token
  // dejaba una sesión con `Bearer undefined` que se ve como que entró.
  //
  // LO QUE ESTE REMIENDO SÍ HACE Y LO QUE NO. Sube el listón de «cualquier
  // servidor web» a «un servidor que imite a TAP»: quien devuelva
  // `{"token":"x"}` sigue pasando. La autorización de verdad —que el aparato
  // sepa con quién habla— es el PASO 5, y esto no la sustituye. Está escrito
  // aquí para que nadie lea esta línea como si cerrara el problema.
  if (typeof r?.token !== "string" || r.token === "") {
    throw new ErrorDelEdge(
      401,
      "la frase no coincide, o del otro lado no hay un servidor de TAP.",
    );
  }
  guardarToken(r.token);
}

export async function quienSoy(): Promise<{
  email: string;
  role: string;
  local: boolean;
  puede_administrar: boolean;
}> {
  return pedir("/auth/yo");
}
